
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import moment, { Moment } from 'moment-timezone';
import AnnouncementDisplay from '@/components/AnnouncementDisplay.vue';
import OrderedQueueDisplay from '@/components/ordered/OrderedQueue.vue';
import AppointmentsQueueDisplay from '@/components/appointments/AppointmentsQueue.vue';
import QueueManage from '@/components/admin/QueueManage.vue';
import ErrorDialog from '@/util/ErrorDialog';
import { DialogProgrammatic as Dialog } from 'buefy';
import { shouldShowCourses } from '@/util/SidebarVisibility';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faCog, faEthernet } from '@fortawesome/free-solid-svg-icons';

library.add(faCog, faEthernet);

@Component({
	components: {
		AnnouncementDisplay,
		OrderedQueueDisplay,
		AppointmentsQueueDisplay,
	},
})
export default class QueuePage extends Vue {
	@Prop({ required: true }) studentView!: boolean;

	found = false;
	loaded = false;
	ws!: WebSocket;
	lastMessage: Moment | undefined;
	time = moment();
	timeUpdater!: number;

	created() {
		// Decide whether to show the sidebar based on our utility function
		this.$root.$data.showCourses = shouldShowCourses(this, this.queue);

		if (this.queue === undefined) {
			this.$buefy.toast.open({
				duration: 5000,
				message: `I couldn't find that queue! Bringing you back home…`,
				type: 'is-danger',
			});

			this.$router.push('/');
			return;
		}

		this.found = true;

		// We need to manually refresh the time every so often
		// as Vue isn't reactive to moment changes. I don't
		// like doing this either.
		this.timeUpdater = window.setInterval(() => {
			this.time = moment();
			if (
				this.lastMessage !== undefined &&
				this.time.diff(this.lastMessage, 'seconds') > 10 + 2
			) {
				location.reload();
			}
		}, 5 * 1000);

		document.title = this.queue.course.shortName + ' Office Hours';

		// Block on WS open so we are connected to receive events
		// *before* getting latest data
		const url = new URL(
			process.env.BASE_URL + `api/queues/${this.queue.id}/ws`,
			window.location.href
		);
		url.protocol = url.protocol.replace('http', 'ws');
		this.ws = new WebSocket(url.href);

		this.ws.onopen = () => {
			this.queue.pullQueueInfo(this.time).then(() => (this.loaded = true));
			/* this.queue */
			/*   .pullQueueInfo() */
			/*   .then(() => setTimeout(() => (this.loaded = true), 5000)); */
		};

		this.ws.onclose = (c) => {
			if (c.code !== 1005) {
				console.log('disconnected:');
				console.log(c);
				this.$buefy.toast.open({
					duration: 5000,
					message:
						'It looks like you got disconnected from the server. Refreshing…',
					type: 'is-danger',
				});
				this.$emit('disconnected');
			}
		};

		this.ws.onmessage = (e) => {
			this.lastMessage = moment();

			const msg = JSON.parse(e.data);
			const type = msg.e;
			const data = msg.d;

			this.queue.handleWSMessage(type, data, this.ws);
		};
	}

	destroyed() {
		if (this.ws !== undefined) {
			this.ws.close();
		}

		clearInterval(this.timeUpdater);
	}

	get queue() {
		return this.$root.$data.queues[this.$route.params.qid] || null;
	}

	get admin() {
		return (
			!this.studentView &&
			this.$root.$data.userInfo !== undefined &&
			this.$root.$data.userInfo.admin_courses !== undefined &&
			this.$root.$data.userInfo.admin_courses.includes(this.queue.course.id)
		);
	}

	openManageDialog() {
		Promise.all([
			fetch(process.env.BASE_URL + `api/queues/${this.queue.id}/configuration`),
			fetch(process.env.BASE_URL + `api/queues/${this.queue.id}/groups`),
		])
			.then(([config, groups]) => Promise.all([config.json(), groups.json()]))
			.then(([configuration, groups]) => {
				this.$buefy.modal.open({
					parent: this,
					component: QueueManage,
					props: {
						defaultConfiguration: configuration,
						defaultGroups: groups,
						type: this.queue.type,
					},
					events: {
						configurationSaved: (
							newConfiguration: {
								[index: string]: any;
							},
							promptsInput: string
						) => {
							if (promptsInput === '') promptsInput = '[]';

							try {
								const prompts = JSON.parse(promptsInput);
								if (
									!Array.isArray(prompts) ||
									!prompts.every((p: any) => typeof p === 'string')
								) {
									throw new Error('Invalid prompt');
								}
								newConfiguration.prompts = prompts;
							} catch (e) {
								return Dialog.alert({
									message:
										'Custom prompts must be a valid JSON array of strings',
									type: 'is-danger',
								});
							}

							fetch(
								process.env.BASE_URL +
									`api/queues/${this.queue.id}/configuration`,
								{
									method: 'PUT',
									body: JSON.stringify(newConfiguration),
								}
							).then((res) => {
								if (res.status !== 204) {
									return ErrorDialog(res);
								}
								this.$buefy.toast.open({
									duration: 5000,
									message: 'Queue settings saved!',
									type: 'is-success',
								});
							});
						},
						groupsSaved: (newGroups: string[][]) => {
							fetch(
								process.env.BASE_URL + `api/queues/${this.queue.id}/groups`,
								{
									method: 'PUT',
									body: JSON.stringify(newGroups),
								}
							).then((res) => {
								if (res.status !== 204) {
									return ErrorDialog(res);
								}
								this.$buefy.toast.open({
									duration: 5000,
									message: 'Queue groups saved!',
									type: 'is-success',
								});
							});
						},
						announcementAdded: (content: string) => {
							fetch(
								process.env.BASE_URL +
									`api/queues/${this.queue.id}/announcements`,
								{ method: 'POST', body: JSON.stringify({ content: content }) }
							).then((res) => {
								if (res.status !== 201) {
									return ErrorDialog(res);
								}
								this.$buefy.toast.open({
									duration: 5000,
									message: 'Announcement added!',
									type: 'is-success',
								});
							});
						},
					},
					hasModalCard: true,
					trapFocus: true,
				});
			});
	}
}
